import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortalContext } from '../../contexts/PortalContext';
import { Loader, AlertCircle, CheckCircle } from 'lucide-react';

const PortalLogin = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login, business, error } = usePortalContext();
  const [status, setStatus] = useState('validating'); // validating, success, error

  // Get branding (available after successful login)
  const brandColor = business?.brandColor || '#3b82f6';
  const businessName = business?.name || 'Customer Portal';
  const logoUrl = business?.logoUrl || null;

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      const success = await login(token);
      
      if (success) {
        setStatus('success');
        // Redirect to dashboard after brief delay
        setTimeout(() => {
          navigate('/portal/dashboard');
        }, 1500);
      } else {
        setStatus('error');
      }
    };

    validateToken();
  }, [token]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: status === 'success' && business
          ? `linear-gradient(135deg, ${brandColor}15, ${brandColor}25)`
          : 'linear-gradient(135deg, #eff6ff, #dbeafe)'
      }}
    >
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            {status === 'success' && logoUrl ? (
              <img 
                src={logoUrl} 
                alt={businessName} 
                className="h-16 w-auto mx-auto mb-4 object-contain"
              />
            ) : status === 'success' && business ? (
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl"
                style={{ backgroundColor: brandColor }}
              >
                {businessName.charAt(0).toUpperCase()}
              </div>
            ) : null}
            
            <h1 
              className="text-2xl font-bold"
              style={{ color: status === 'success' ? brandColor : '#7c3aed' }}
            >
              {status === 'success' ? businessName : 'Customer Portal'}
            </h1>
            <p className="text-gray-600 mt-2">Secure access to your account</p>
          </div>

          {/* Status Display */}
          {status === 'validating' && (
            <div className="text-center py-8">
              <Loader size={48} className="mx-auto text-blue-600 mb-4 animate-spin" />
              <p className="text-gray-600">Verifying your access link...</p>
              <p className="text-sm text-gray-500 mt-2">Please wait a moment</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${brandColor}20` }}
              >
                <CheckCircle size={32} style={{ color: brandColor }} />
              </div>
              <p className="text-lg font-medium text-gray-900">Access Granted!</p>
              <p className="text-gray-600 mt-2">Redirecting to your dashboard...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <p className="text-lg font-medium text-gray-900">Access Denied</p>
              <p className="text-gray-600 mt-2">
                {error || 'This link is invalid or has expired.'}
              </p>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Need help?</strong>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Contact the business that sent you this link to request a new access link.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Security Note */}
        <p className="text-center text-sm text-gray-600 mt-6">
          🔒 Your connection is secure and encrypted
        </p>
        
        {/* Powered by - only show on success with branding */}
        {status === 'success' && business && (
          <p className="text-center text-xs text-gray-400 mt-2">
            Powered by SuddyFlow
          </p>
        )}
      </div>
    </div>
  );
};

export default PortalLogin;